import {postRequest, getRequest} from '../../utils/api'
import Message from 'element-ui/lib/message'
import eventBus from '../eventBus'

// 街道移动域：罗盘/地图移动 + 街道场景数据（节点、NPC、任务、道具、商店、玩家列表）
// 数据驱动移动：moveTo 成功后直接更新本地状态并广播，不再整页重载
// 场景快照落地：moveTo（走一格）与 ride（乘车）共用。
// 两者服务端返回的是同一种结构，唯一差别是「走一格」要播滑屏动画、乘车播自己的报站过场，
// 所以动画留在外面，这里只管把数据写进 state 并广播。
function applySceneSnapshot(commit, state, data) {
  commit('SET_STREET_DATA', {
    cityMap: data.cityMap,
    city: data.city,
    itemList: data.itemList,
    userPositions: data.userPositions,
    specialEvents: data.specialEvents,
    userHeader: data.userHeader
  });
  // 职业地图事件：主动移动触发时顶部横幅提示（非阻断）
  if (data.careerEvent) {
    eventBus.$emit('careerEvent', data.careerEvent);
  }
  // 兼容广播：现有组件（header/pocket/streetleft 等）通过 eventHub 订阅
  eventBus.$emit('currentUser', data.fantasyUserBO);
  eventBus.$emit('cityMap', state.cityMap);
  // 所属城市：聊天区据此判断是否跨城（王都 ⇄ 幻之都）。
  // 跨城时 chat.vue 要清空城市频道并重新拉取新城市的消息，否则聊天记录会把两个城市混在一起。
  if (data.city || data.cityMap) {
    eventBus.$emit('cityInfo', {city: data.city, cityMap: data.cityMap || state.cityMap});
  }
  eventBus.$emit('refreshFantasyUserHeader', data.userHeader);
  // 玩家属性收拢：同时写入 user 模块（组件 mapState 订阅）
  commit('user/SET_FANTASY_USER', data.fantasyUserBO, {root: true});
  commit('user/SET_USER_HEADER', data.userHeader, {root: true});
  eventBus.__cityMapCache = state.cityMap;
  // 节点 NPC/待办任务与商店：后端已随场景快照一并下发，不再单独请求。
  // 原先前端要在 moveTo 之后串行发 getTaskTodoList + getShop，这两个接口后端只有 ~15ms，
  // 但每次请求都要在公网链路上多付一次约 280ms 的固定成本（首尔 nginx → frp → entry → gateway），
  // 一次移动因此被拉成「788ms + 296ms」两段。
  // 之所以不能简单改成「异步不阻塞滑入」：NPC 的 DOM 完全由 npclist 渲染
  // （npcFloat 里 v-for="npc in npclist"），异步会先看到空场景、NPC 再整批冒出来。
  commit('SET_NPC_TASK', {
    npclist: data.npclist || [],
    fantasyTaskTodoBOList: data.fantasyTaskTodoBOList || []
  });
  commit('SET_SHOP', data.fantasyMapShopBO || null);
  // 另：/user/user/sparkUserAbility 已不再调用。该接口判据是 map_trigger === '1'，
  // 而 fantasy_title_ability 现网 16 行里 map_trigger 只有 0 和节点 id 列表两种值，
  // 无一行等于 1（2026-09-16 改成节点列表后失效），属纯空转。
  // 若日后恢复按地图触发能力，应由移动的返回体直接下发结果，不要再单独发请求。
}

export default {
  namespaced: true,
  state: {
    cityMap: {},
    city: {},
    npclist: [],
    fantasyTaskTodoBOList: [],
    // 任务列表弹窗展示专用：/task/task/getTaskList 返回的是中文名（taskFrom/taskTo/taskItem 已转换），
    // 而给道具/交谈/回任务等判定一律按 fantasyTaskTodoBOList 里的裸 id 与 npcId 比对，两者不可混用。
    taskListDisplay: [],
    itemList: [],
    userPositions: [],
    fantasyMapShopBO: null,
    specialEvents: [],
    moving: false,
    moveDirection: ''
  },
  mutations: {
    SET_STREET_DATA(state, payload) {
      state.cityMap = payload.cityMap || state.cityMap;
      state.city = payload.city || state.city;
      state.itemList = payload.itemList || [];
      state.userPositions = payload.userPositions || [];
      state.specialEvents = payload.specialEvents || [];
    },
    SET_NPC_TASK(state, payload) {
      state.npclist = payload.npclist || [];
      state.fantasyTaskTodoBOList = payload.fantasyTaskTodoBOList || [];
    },
    SET_SHOP(state, shop) {
      state.fantasyMapShopBO = shop;
    },
    SET_NPCLIST(state, npclist) {
      state.npclist = npclist || [];
    },
    SET_TASK_LIST(state, list) {
      state.fantasyTaskTodoBOList = list || [];
    },
    SET_TASK_LIST_DISPLAY(state, list) {
      state.taskListDisplay = list || [];
    },
    SET_MOVING(state, payload) {
      state.moving = payload.moving;
      if (payload.direction !== undefined) {
        state.moveDirection = payload.direction;
      }
    }
  },
  actions: {
    // 乘车到站：把服务端返回的场景快照落地。
    // 过场动画由 coachPanel 自己播（报站是它的事），这里只负责数据 —— 所以不碰 moving，
    // 否则会触发 streetmain 的滑屏动画，变成「坐车也在走一格」的观感。
    applyRideScene({state, commit}, data) {
      if (!data) {
        return {success: false};
      }
      applySceneSnapshot(commit, state, data);
      return {success: true};
    },
    // 从当前位置移动到目标节点；targetId 传 '-1' 时后端按缓存 curPoint 恢复（首登/刷新）
    moveTo({state, commit}, {targetId, direction}) {
      if (state.moving) {
        return Promise.resolve({success: false});
      }
      commit('SET_MOVING', {moving: true, direction: direction || ''});
      return postRequest('/map/move/moveTo', {
        fromId: state.cityMap.id || '-1',
        targetId: targetId
      }).then(resp => {
        if (resp.status == 200) {
          const respData = resp.data;
          if (200 == respData.status) {
            const data = respData.data;
            applySceneSnapshot(commit, state, data);
            return {success: true};
          } else {
            Message.error(respData.message);
            return {success: false};
          }
        } else {
          Message.error('用户非法⊙﹏⊙∥!');
          return {success: false};
        }
      }, () => {
        Message.error('用户非法⊙﹏⊙∥!');
        return {success: false};
      }).then(result => {
        commit('SET_MOVING', {moving: false});
        return result;
      });
    },
    // 首登/刷新恢复：后端按缓存 curPoint 恢复位置
    initStreet({dispatch}) {
      return dispatch('moveTo', {targetId: '-1', direction: ''});
    },
    // 拉取目标节点的 NPC 与任务待办列表
    // ⚠ moveTo 已改为随场景快照下发（见 moveTo 里的 SET_NPC_TASK），这里保留仅用于
    // 「不移动、但需要单独刷新当前节点 NPC/任务标记」的场景，目前无调用者。
    // 后端 /task/task/getTaskTodoList 接口保留未删。
    fetchStreetData({commit}, targetId) {
      return postRequest('/task/task/getTaskTodoList', {
        targetId: targetId
      }).then(resp => {
        if (resp.status == 200) {
          const respData = resp.data;
          if (200 == respData.status) {
            commit('SET_NPC_TASK', {
              npclist: respData.data ? respData.data.npclist : [],
              fantasyTaskTodoBOList: respData.data ? respData.data.fantasyTaskTodoBOList : []
            });
          } else {
            Message.error(respData.message);
          }
        } else {
          Message.error('NPC获取失败');
        }
      }, () => {
        Message.error('NPC获取异常');
      });
    },
    // 拉取目标节点商店信息
    // ⚠ moveTo 已改为随场景快照下发（见 moveTo 里的 SET_SHOP），这里保留仅用于单独刷新，
    // 目前无调用者。后端 /item/shop/selectFantasyMapShopByMapId 接口保留未删。
    fetchShop({commit}, mapId) {
      return getRequest('/item/shop/selectFantasyMapShopByMapId', {
        mapId: mapId
      }).then(resp => {
        if (resp.status == 200) {
          const respData = resp.data;
          if (200 == respData.status) {
            commit('SET_SHOP', respData.data || null);
          } else {
            Message.error(respData.message);
          }
        } else {
          Message.error('商店信息获取失败');
        }
      }, () => {
        Message.error('商店信息获取异常');
      });
    },
    // 刷新玩家当前任务列表（getTaskListWithoutTransFor），替代各组件重复请求
    fetchTaskList({commit}) {
      return getRequest('/task/task/getTaskListWithoutTransFor', {
      }).then(resp => {
        if (resp.status == 200) {
          //成功
          const respData = resp.data;
          if (200 == respData.status) {
            commit('SET_TASK_LIST', respData.data ? respData.data.fantasyTaskTodoBOList : []);
          } else {
            Message.error(respData.message);
          }
        } else {
          //失败
          Message.error('用户非法⊙﹏⊙∥!');
        }
      }, () => {
        Message.error('用户非法⊙﹏⊙∥!222');
      });
    }
  }
}